import {useEffect, useRef, useState} from 'react'
import {useMapConfig} from '@/hooks/useAppConfig.js'
import {decodePolyline, metresBetween, metresToPath, toPoint} from '@/utils/polyline'
import {isDebug} from '@/utils/debug'

/**
 * THE RUN — one Directions call covering the driver and every drop still to be made.
 *
 * ── Why it routes the whole run, not just the next hop ────────────────────────────
 * This replaced a two-point hook (`useApproachRoute`, now deleted) that solved only the
 * driver and the pickup, on the reading that the en-route screen asks one question: how do
 * I reach the passenger. That left a driver unable to see where the ride went after the
 * pickup — a trip with stop-overs drew one marker and no hint that three more places
 * existed.
 *
 * This solves the WHOLE remaining run — `driver;drop1;drop2;…;final` — in a single
 * request, which is what a multi-stop ride needs and what a two-point call structurally
 * cannot give:
 *
 *   PER-DROP ETAs      Directions returns one `leg` per hop, so drop 3's arrival time is
 *                      the sum of legs 0..2. Solving hop by hop as the driver reaches
 *                      each one leaves every later drop with no time against it until it
 *                      becomes the next one — which is precisely the number a dispatcher
 *                      and a waiting passenger are asking for.
 *   ONE LINE           The whole run draws as a single polyline, so the driver sees the
 *                      shape of the rest of the run, not just the next hop.
 *   ONE REQUEST        N drops for the price of one call rather than N.
 *
 * ── ⚠️ 25 COORDINATES IS THE CEILING ──────────────────────────────────────────────
 * The `driving` profile takes at most 25 coordinates per request. A ride will never come
 * close, but the cap is enforced here rather than trusted: over it Directions returns an
 * error and this screen would silently lose its route. Truncating keeps the near drops —
 * the ones being driven now — routed and timed.
 *
 * ── Re-solving: the same three triggers as the approach ───────────────────────────
 * A Directions request on every GPS fix would be one call every two seconds per driver
 * for a road that has not changed. So the run is re-solved when
 *
 *   the MANIFEST changed  a drop was completed, so the run is one hop shorter
 *   the driver STRAYED    `OFF_ROUTE_METERS` off the solved line — a DIFFERENT ROAD
 *   the driver MOVED      `RECALC_METERS` along — a slow refresh, not a progress tracker
 *
 * ⚠️ STRAYING IS THE REAL TRIGGER. `RECALC_METERS` was 120m, which at city speed fired a
 * fresh Directions call every ten to fifteen seconds — and every solve is free to return a
 * slightly different road, so the guidance kept changing while the driver drove one
 * unchanging street. That is not navigation, it is a route flickering between equally good
 * answers. A driver ON their route keeps it; that is what every navigation app does and
 * what the eye expects.
 *
 * So the movement trigger is now a SLOW REFRESH — far enough apart that traffic and ETA do
 * not go stale on a long leg, never often enough to re-argue a road the driver is already
 * on. Turning off the route is what re-solves, and it re-solves immediately.
 *
 * ── Failure is silent, and the caller decides ─────────────────────────────────────
 * Anything other than a route leaves whatever is on screen alone and clears the
 * solved-from marker so the NEXT fix retries — rather than waiting out RECALC_METERS, or,
 * on a permanent failure like a bad token, never trying again.
 *
 * @param {{lat: number, lng: number}|null} rawDriver  live position
 * @param {Array<{point: {lat: number, lng: number}}>} drops  remaining drops, in order
 */

/**
 * Move this far (metres) along the solved run before re-solving it.
 *
 * ⚠️ THIS IS A STALENESS BOUND, NOT A TRACKING INTERVAL. It was 120m, i.e. a new route
 * every ~14s at 30km/h, and re-solving that often is what made the guidance unstable: each
 * response can pick a different-but-equivalent road, so the line and the next instruction
 * changed under a driver who had done nothing but keep going straight.
 *
 * 1500m is roughly two minutes of city driving — often enough that traffic and the ETA stay
 * honest on a long leg, rare enough that it is never the reason the route changes. Real
 * deviations are caught by `OFF_ROUTE_METERS` at once, which is the trigger that matters.
 */
const RECALC_METERS = 1500

/**
 * Stray this far (metres) off the solved line and it re-solves at once, whatever the
 * movement gate says. 60m sits above city GPS noise (a phone reports 10-30m) and above
 * honest parallax on a divided highway, while still tripping on the first missed turn.
 */
const OFF_ROUTE_METERS = 60

/** Mapbox's own limit for this profile, the driver's own position included. */
const MAX_COORDINATES = 25

/**
 * ⚠️ `driving-traffic`, NOT `driving` — this is what makes the ETAs and the line real.
 *
 * `driving` routes on typical speeds: a run solved at 5pm and the same run solved at 3am
 * come back with the same duration, which is not a number anyone can dispatch against.
 * `driving-traffic` uses live and historical conditions, and it is also the only profile
 * that populates the `congestion` annotation the route line is coloured from.
 *
 * ⚠️ IT ONCE TOOK ONLY 3 COORDINATES, and plenty of guidance still says so — the SDK
 * reference carried that limit as recently as late 2024. The Directions API now documents
 * 25 for this profile, the same as the others, which is what makes routing a whole
 * manifest through it possible at all. If a long run ever starts coming back refused,
 * this is the first thing to check: the fallback is `driving`, which loses the congestion
 * colouring but keeps the run routed.
 */
const PROFILE = 'driving-traffic'

const TAG = 'color:#0f9d76;font-weight:bold'
/**
 * ⚠️ OFF BY DEFAULT. These lines exist for working on this file and they bury the
 * one thing the console is usually being read for — whether the driver's position is
 * arriving. The call sites are left in place: set `hopnMapDebug = true` in the console
 * and reload to bring them back, rather than editing code to debug code.
 */
/**
 * The furthest apart two points on one run can be before the request is not worth making.
 *
 * ⚠️ WELL ABOVE ANY REAL RIDE AND WELL BELOW A TEST ARTEFACT. The longest journey this
 * product sells is a few hundred kilometres; a simulated driver in the wrong city is
 * thousands. 1500km leaves the first alone and catches the second.
 */
const MAX_DRIVABLE_KM = 1500

const log = (...args) => {
    if (isDebug('hopnMapDebug')) console.log('%c[manifest]', TAG, ...args)
}

const EMPTY = {
    polyline: null,
    legs: [],
    maneuver: null,
    congestion: null,
    voice: null,
    totalDistanceText: null,
    totalDurationText: null,
    totalDurationSec: 0,
}

const km = (metres) => `${(metres / 1000).toFixed(1)} km`
const mins = (seconds) => `${Math.round(seconds / 60)} min`

export default function useManifestRoute(rawDriver, drops) {
    const {token} = useMapConfig()
    const driver = toPoint(rawDriver)
    const points = (drops || [])
        .map((d) => toPoint(d?.point))
        .filter(Boolean)
        .slice(0, MAX_COORDINATES - 1)
    const [route, setRoute] = useState(EMPTY)

    // What the current line was solved FROM and THROUGH. Refs, not state: they gate the
    // effect without being able to re-trigger it themselves.
    const solvedFromRef = useRef(null)
    const solvedRunRef = useRef(null)
    /**
     * A solve is out on the wire.
     *
     * ⚠️ THIS IS WHAT MAKES A SLOW RESPONSE SURVIVE. The effect re-runs on every GPS fix —
     * every 2s — and its cleanup used to set `cancelled`, throwing away any request still in
     * flight. But the guard refs were written BEFORE the request, so the retry gate then read
     * "already solved this run, and we have not moved far enough to re-solve" and returned
     * early forever. A Directions call slower than one GPS tick was therefore discarded and
     * never retried: the leg after Leave Pickup had no line until the page was reloaded and
     * the refs started empty.
     *
     * Holding the request instead of cancelling it means one solve at a time, and movement
     * during it is simply ignored rather than being allowed to kill it.
     */
    const inFlightRef = useRef(false)

    /** Unmounted. The only thing that should ever discard a solve that has come back. */
    const goneRef = useRef(false)
    useEffect(() => {
        goneRef.current = false
        return () => { goneRef.current = true }
    }, [])
    // The decoded geometry currently on screen — what "off route" is measured against.
    const solvedPathRef = useRef([])

    /* The manifest as one string. It is both the change detector and the request's own
       coordinate list, so the two can never disagree about what is being routed. */
    const run = points.map((p) => `${p.lng},${p.lat}`).join(';')

    useEffect(() => {
        if (!token || !driver || !run) {
            // Which input is missing IS the diagnosis: no driver means there is no GPS fix
            // yet, no run means every drop is done (or none carried coordinates), no token
            // means /config/app has not landed.
            log('waiting —', {
                token: !!token,
                driverFix: driver ? `${driver.lat},${driver.lng}` : null,
                drops: points.length,
            })
            return undefined
        }

        const runChanged = solvedRunRef.current !== run
        const movedEnough = !solvedFromRef.current
            || metresBetween(solvedFromRef.current, driver) > RECALC_METERS

        const strayed = solvedPathRef.current.length > 0
            ? metresToPath(driver, solvedPathRef.current)
            : 0
        const offRoute = strayed > OFF_ROUTE_METERS
        if (offRoute) log(`off route by ${Math.round(strayed)}m — re-solving from here`)

        if (!runChanged && !movedEnough && !offRoute) return undefined

        // One at a time. A `run` that changes mid-flight is not lost: the refs still hold
        // the old one, so `runChanged` stays true and the next fix — 2s away — solves it.
        if (inFlightRef.current) return undefined

        inFlightRef.current = true
        solvedFromRef.current = {lat: driver.lat, lng: driver.lng}
        solvedRunRef.current = run

        /*
         * ⚠️ MAPBOX TAKES LNG,LAT — the reverse of every other coordinate in this
         * codebase. Getting it backwards does not error; it routes between two
         * plausible-looking places in the wrong hemisphere.
         *
         * `geometries=polyline` (NOT polyline6) so the response decodes with the same
         * `decodePolyline` the map uses; polyline6 is precision 6 and would come out ten
         * times too small without complaining. `overview=full` because this line is drawn
         * at street zoom, where the default `simplified` visibly cuts corners.
         *
         * `steps` + `banner_instructions` are what make this navigation rather than a
         * drawn line: Mapbox returns a maneuver per step plus its own composed banner
         * text. Composing that ourselves from type + modifier + road name gets the grammar
         * wrong in every language but English.
         *
         * `voice_instructions` is the same argument for the EAR. Mapbox returns, per step,
         * a list of announcements each with the distance before the maneuver at which to
         * say it ("In 400 metres, turn right onto Shah Jilani Road" … "Turn right"). That
         * cadence — what to say, and when — is the part of spoken guidance that is hard to
         * get right, and it is exactly what composing our own from the banner text would
         * throw away. `voice_units=metric` matches the distances shown on screen; a screen
         * reading 400 m while the speaker says quarter of a mile is worse than silence.
         *
         * `annotations=congestion` is what colours the line. It arrives per LEG, one entry
         * per geometry segment, and needs `overview=full` — a simplified overview has
         * fewer points than the annotation has entries, so the two would not line up.
         */
        /*
         * ⚠️ A RUN NOBODY COULD DRIVE IS NOT SENT AT ALL.
         *
         * Mapbox answers a car route between two continents with 422, twice — once for the
         * constrained attempt and once for the retry — and both land in the console as red
         * network errors that cannot be caught or silenced, because the browser logs a
         * failed response before any code sees it.
         *
         * It happens constantly in testing, where a simulated driver sits in one city and
         * the trip's stops are in another, and it is indistinguishable at a glance from a
         * real fault. Real rides never approach this distance: the longest journey this
         * product sells is a few hundred kilometres.
         */
        const spanKm = metresBetween(driver, points[0]) / 1000
        if (spanKm > MAX_DRIVABLE_KM) {
            /* ⚠️ ALWAYS SHOWN, unlike the rest of this file's logging. A map with no route
               on it is the loudest symptom this hook has, and leaving the reason behind a
               debug flag means the next person sees an empty map and starts looking for a
               bug in the map. The car is simply not where the ride is. */
            console.warn('%c[manifest]', TAG,
                `no route drawn — the driver is ${Math.round(spanKm)} km from this trip's first stop.`,
                {driver, firstStop: points[0]})
            return undefined
        }

        const coords = `${driver.lng},${driver.lat};${run}`
        const base = `https://api.mapbox.com/directions/v5/mapbox/${PROFILE}/${coords}`
            /* ⚠️ NO `voice_instructions` — this is the passenger portal. Mapbox returns a
               spoken script per step, and asking for it here would be paying for a payload
               nothing can read aloud: there is no voice hook on this side, and no mute
               button to turn one off. `banner_instructions` stays, because the map's own
               banner does show the next turn. */
            + '?geometries=polyline&overview=full&steps=true&banner_instructions=true'
            + '&annotations=congestion'
            + `&access_token=${encodeURIComponent(token)}`

        /*
         * ── KEEPING THE LINE ON REAL ROADS ────────────────────────────────────────
         *
         * ⚠️ THERE IS NO "PUBLIC ROADS ONLY" FLAG, and it is worth being straight with
         * anyone who asks for one. Mapbox routes on OpenStreetMap's network: if a service
         * road through a compound is mapped as drivable, the router is entitled to use it,
         * and no request parameter overrides that. A route cutting through a campus is the
         * DATA being wrong, not the API — and the fix for it is upstream, in OSM.
         *
         * These two are what the API does offer, and both are real improvements:
         *
         *   exclude=unpaved   drops dirt tracks and unsurfaced service roads, which is what
         *                     most "it routed me through a building" cases actually are.
         *
         *   approaches        `curb` on every DROP so arrival lands on the driving side of
         *                     the road rather than across it. The first coordinate is the
         *                     DRIVER and stays unrestricted — the car is already wherever it
         *                     is, and constraining its side can refuse the whole route.
         */
        const approaches = ['unrestricted', ...points.map(() => 'curb')].join(';')
        const url = `${base}&exclude=unpaved&approaches=${approaches}`

        log('solving —', `${points.length} drop(s) from ${driver.lat},${driver.lng}`)

        /*
         * ⚠️ THE CONSTRAINTS CAN MAKE A RUN UNROUTABLE, and then they cost more than they
         * buy — a driver with no line at all is worse off than one with a line down a
         * rough road. Plenty of perfectly ordinary streets are tagged unpaved in OSM, and
         * `curb` can refuse a drop that only has access from the far side.
         *
         * So they are a PREFERENCE, not a requirement: one retry without them, and the run
         * routes. The retry is deliberately not itself retried — a second failure is about
         * the route, not the constraints.
         */
        const solve = () => fetch(url)
            .then((res) => res.json())
            .then((data) => {
                if (data?.routes?.[0]?.geometry) return data
                log('refused with road constraints — retrying unconstrained', data?.code || '')
                return fetch(base).then((res) => res.json())
            })

        solve()
            .then((data) => {
                /* ⚠️ THE ONLY REASONS TO DROP A RESULT: the screen is gone, or the run this
                   was solved for is no longer the run being driven. NOT "the effect re-ran",
                   which is what a `cancelled` closure flag meant here — and since this effect
                   re-runs on every GPS fix, that discarded every solve slower than 2s while
                   the guard refs still said it had been done. The leg after Leave Pickup then
                   had no line until a reload reset those refs. */
                if (goneRef.current || solvedRunRef.current !== run) return
                const best = data?.routes?.[0]
                if (!best?.geometry) {
                    /*
                     * Leave whatever is on screen alone — blanking the run because one
                     * re-solve failed would drop the line the driver is following.
                     *
                     * But CLEAR the solved-from marker so the next fix retries, rather
                     * than waiting out RECALC_METERS. Without this a single failure at the
                     * start of a run means no route until the driver has already covered
                     * 120m, and a PERMANENT failure (a bad token) is tried exactly once.
                     */
                    solvedFromRef.current = null
                    log(
                        `REFUSED — ${data?.code || 'no route'}.`,
                        data?.message || 'Keeping the previous line; will retry on the next fix.',
                    )
                    return
                }

                solvedPathRef.current = decodePolyline(best.geometry)

                /*
                 * ONE LEG PER HOP, in manifest order — the whole reason for routing the
                 * run in a single call. `legs[i]` is the drive to drop `i`, so the panel
                 * can put a distance and an arrival time against EVERY remaining drop by
                 * accumulating them, not just against the next one.
                 */
                let cumulative = 0
                const legs = (best.legs || []).map((leg) => {
                    cumulative += leg.duration || 0
                    return {
                        distanceM: leg.distance || 0,
                        durationSec: leg.duration || 0,
                        distanceText: km(leg.distance || 0),
                        durationText: mins(leg.duration || 0),
                        // Seconds from NOW until this drop is reached. Seconds rather than
                        // a clock time so the panel can re-print it against the current
                        // moment on every render and it stays right as the drive proceeds.
                        etaSec: cumulative,
                    }
                })

                /*
                 * THE NEXT MANEUVER — step 0 of leg 0, the hop being driven.
                 *
                 * The run is re-solved FROM THE DRIVER (every 120m, and immediately when
                 * they leave the line), so its first step is always the instruction being
                 * executed right now. That is what makes a single step enough: there is no
                 * progress to track through a step list, because the list is rebuilt from
                 * where the driver actually is.
                 */
                const step = best.legs?.[0]?.steps?.[0]
                const banner = step?.bannerInstructions?.[0]?.primary
                const at = step?.maneuver?.location
                const maneuver = step ? {
                    text: banner?.text || step.maneuver?.instruction || '',
                    type: step.maneuver?.type || '',
                    modifier: step.maneuver?.modifier || '',
                    distanceM: step.distance || 0,
                    distanceText: step.distance >= 1000
                        ? `${(step.distance / 1000).toFixed(1)} km`
                        : `${Math.round(step.distance || 0)} m`,
                    /*
                     * WHERE THE TURN IS, as a point — `maneuver.location`, which Mapbox
                     * sends [lng, lat] like every other coordinate it produces.
                     *
                     * This is what lets the screen measure the distance to the turn on
                     * EVERY GPS fix instead of only on a re-solve. `distanceM` above is
                     * frozen at the moment the route was solved, and the route is solved
                     * once per 120m — so a countdown driven by it would sit still for a
                     * block at a time and then jump, and the voice would announce "turn
                     * right" a hundred metres after the junction.
                     */
                    point: Array.isArray(at) ? {lat: Number(at[1]), lng: Number(at[0])} : null,
                } : null

                /*
                 * CONGESTION, FLATTENED ACROSS LEGS — one entry per segment of the whole
                 * geometry, so it indexes straight into the decoded polyline.
                 *
                 * The arrays concatenate cleanly because a leg's annotation has one entry
                 * per segment (points − 1) and consecutive legs share their boundary
                 * point: summing (points − 1) over the legs is exactly the segment count
                 * of the joined geometry. Off-by-one here does not error — it shifts every
                 * colour one segment along the road, which looks like the map is reporting
                 * traffic slightly in the wrong place.
                 */
                const congestion = (best.legs || []).flatMap((leg) => leg.annotation?.congestion || [])

                log(
                    'solved —',
                    `${km(best.distance || 0)} / ${mins(best.duration || 0)} over ${legs.length} leg(s)`,
                    maneuver?.text || '',
                )
                setRoute({
                    polyline: best.geometry,
                    legs,
                    maneuver,
                    congestion: congestion.length > 0 ? congestion : null,
                    /*
                     * THE ANNOUNCEMENTS FOR THE STEP BEING DRIVEN, untouched.
                     *
                     * Each carries `distanceAlongGeometry` — how far BEFORE the end of the
                     * step to say it — and Mapbox's own phrasing. The voice hook decides
                     * which one is due; deciding that here would freeze it to the moment
                     * of the solve, which is the whole thing `maneuver.point` exists to
                     * avoid.
                     */
                    voice: null,
                    totalDistanceText: km(best.distance || 0),
                    totalDurationText: mins(best.duration || 0),
                    totalDurationSec: best.duration || 0,
                })
            })
            .catch((err) => {
                if (goneRef.current) return
                solvedFromRef.current = null
                log('REQUEST FAILED —', err?.message || 'unknown error')
            })
            .finally(() => { inFlightRef.current = false })

        /* ⚠️ NO CLEANUP THAT CANCELS. React re-runs this effect on every fix; a cleanup that
           marked the in-flight request dead therefore killed it 2s after it started. What
           replaces it is a check at the point of USE — see the guard in `.then` — so a solve
           is dropped only when it is genuinely stale, never merely because time passed. */
        return undefined
    }, [token, driver?.lat, driver?.lng, run])

    return route
}

export {useManifestRoute}
